import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Card, Field, Input, Textarea, Select, PrimaryButton, GhostButton } from "@/components/Primitives";
import StatusPill from "@/components/StatusPill";
import { ArrowLeft, Package, FileText, Question, Cube, FolderOpen, DownloadSimple } from "@phosphor-icons/react";

const SAMPLE_STATUSES = ["Requested", "In Progress", "Shipped", "Received", "Passed", "Failed"];
const DELIV_STATUSES = ["Requested", "In Progress", "Submitted", "Delivered", "Completed"];
const DEP_STATUSES = ["Awaiting Sample Approval", "Cleared", "Blocked", "At Risk"];
const READY_STATUSES = ["Not Ready", "In Progress", "Ready"];
const COMPLIANCE_STATUSES = ["Pending", "In Progress", "Submitted", "Complete", "Missing", "Incomplete"];

export default function SupplierAssignmentDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  const load = () =>
    api.get(`/supplier/assignments/${id}`).then((r) => setData(r.data)).catch((e) => setErr(e?.response?.status || "error"));

  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (err) return (
    <div className="p-8" data-testid="supplier-assignment-error">
      <GhostButton onClick={() => nav("/supplier")} data-testid="back-to-portal"><ArrowLeft size={13} /> Back</GhostButton>
      <div className="mt-6 text-zinc-400 text-sm">This assignment is not available to your account.</div>
    </div>
  );
  if (!data) return <div className="p-8 text-zinc-500 text-xs tracking-widest uppercase">Loading assignment…</div>;

  const opp = data.opportunity || {};
  const a = data.assignment || {};

  return (
    <div data-testid="supplier-assignment-detail">
      <div className="border-b border-white/10 px-8 py-6">
        <button onClick={() => nav("/supplier")} data-testid="back-to-portal"
          className="text-zinc-400 hover:text-[#FFB300] text-[10px] tracking-widest uppercase inline-flex items-center gap-1 mb-3">
          <ArrowLeft size={13} /> Assignment Room
        </button>
        <div className="flex items-end justify-between gap-4">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-1.5">{opp.product_category} · {opp.division}</div>
            <h1 className="font-heading text-3xl font-bold tracking-tighter uppercase">{opp.opportunity_name}</h1>
          </div>
          <StatusPill value={a.assignment_status} size="lg" />
        </div>
      </div>

      <div className="p-8 space-y-7 max-w-[1100px]">
        {/* Assignment overview */}
        <Card className="p-6" data-testid="assignment-overview">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5 text-xs">
            <Meta label="Country" value={opp.country} />
            <Meta label="Product Category" value={opp.product_category} />
            <Meta label="Quantity" value={opp.quantity} mono />
            <Meta label="Type" value={opp.opportunity_type} />
            <Meta label="Tender Deadline" value={opp.deadline} mono />
            <Meta label="Q&A Deadline" value={opp.qa_deadline} mono />
            <Meta label="Sample Due" value={a.sample_due_date} mono />
            <Meta label="Response Due" value={a.supplier_response_due_date} mono />
          </div>
          {opp.supplier_brief && (
            <div className="mt-5 pt-5 border-t border-white/10">
              <div className="text-[10px] tracking-widest uppercase text-zinc-500 mb-1.5">Instructions From +++ Team</div>
              <div className="text-sm text-zinc-200 leading-relaxed" data-testid="supplier-brief">{opp.supplier_brief}</div>
            </div>
          )}
          {opp.official_tender_link && (
            <a href={opp.official_tender_link} target="_blank" rel="noreferrer"
              className="inline-block mt-4 text-[#00E5FF] text-xs underline">Official tender notice ↗</a>
          )}
        </Card>

        <SectionTitle icon={Package} label="Samples" />
        {data.samples.length === 0 ? <Empty>No samples requested yet.</Empty> :
          data.samples.map((s) => <SampleCard key={s.id} sample={s} onSaved={load} />)}

        <SectionTitle icon={FileText} label="Deliverables" />
        {data.deliverables.length === 0 ? <Empty>No deliverables assigned yet.</Empty> :
          data.deliverables.map((d) => <DeliverableCard key={d.id} deliverable={d} onSaved={load} />)}

        <SectionTitle icon={Cube} label="Production Readiness" />
        {data.production.length === 0 ? <Empty>No production tracker yet.</Empty> :
          data.production.map((p) => <ProductionCard key={p.id} prod={p} onSaved={load} />)}

        <SectionTitle icon={Question} label="Questions & Answers" />
        <QuestionsPanel questions={data.questions} opportunityId={opp.id} onSaved={load} />

        <SectionTitle icon={FolderOpen} label="Documents" />
        <DocsPanel docs={data.documents} />
      </div>
    </div>
  );
}

function Meta({ label, value, mono }) {
  return (
    <div>
      <div className="text-[10px] tracking-widest uppercase text-zinc-500 mb-1">{label}</div>
      <div className={`text-zinc-200 ${mono ? "font-mono-data" : ""}`}>{value ?? <span className="text-zinc-700">—</span>}</div>
    </div>
  );
}

function SectionTitle({ icon: Icon, label }) {
  return (
    <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-400 flex items-center gap-2 pt-1">
      <Icon size={14} className="text-[#FFB300]" /> {label}
    </div>
  );
}

function Empty({ children }) {
  return <Card className="p-5 text-center text-zinc-600 text-[11px] tracking-widest uppercase">{children}</Card>;
}

function SampleCard({ sample, onSaved }) {
  const [f, setF] = useState({
    sample_status: sample.sample_status || "Requested",
    tracking_number: sample.tracking_number || "",
    expected_ship_date: sample.expected_ship_date || "",
    estimated_arrival_date: sample.estimated_arrival_date || "",
    supplier_comment: sample.supplier_comment || "",
  });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/supplier/samples/${sample.id}`, f);
      toast.success("Sample updated");
      onSaved();
    } catch (e) { toast.error("Could not update sample"); }
    finally { setSaving(false); }
  };
  return (
    <Card className="p-5" data-testid={`sample-card-${sample.id}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="text-white font-medium text-sm">{sample.sample_name}</div>
        <div className="text-[11px] text-zinc-500 font-mono-data">Qty {sample.quantity_requested ?? "—"} · Due {sample.sample_due_date || "—"}</div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Sample Status">
          <Select value={f.sample_status} onChange={(e) => setF({ ...f, sample_status: e.target.value })} data-testid={`sample-status-${sample.id}`}>
            {SAMPLE_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </Select>
        </Field>
        <Field label="Tracking Number">
          <Input value={f.tracking_number} onChange={(e) => setF({ ...f, tracking_number: e.target.value })} data-testid={`sample-tracking-${sample.id}`} />
        </Field>
        <Field label="Expected Ship Date">
          <Input type="date" value={f.expected_ship_date} onChange={(e) => setF({ ...f, expected_ship_date: e.target.value })} data-testid={`sample-ship-${sample.id}`} />
        </Field>
        <Field label="Estimated Arrival Date">
          <Input type="date" value={f.estimated_arrival_date} onChange={(e) => setF({ ...f, estimated_arrival_date: e.target.value })} data-testid={`sample-arrival-${sample.id}`} />
        </Field>
        <Field label="Comment to +++ Team" className="md:col-span-2">
          <Textarea rows={2} value={f.supplier_comment} onChange={(e) => setF({ ...f, supplier_comment: e.target.value })} data-testid={`sample-comment-${sample.id}`} />
        </Field>
      </div>
      <div className="mt-4">
        <PrimaryButton onClick={save} disabled={saving} data-testid={`sample-save-${sample.id}`}>{saving ? "Saving…" : "Save Sample"}</PrimaryButton>
      </div>
    </Card>
  );
}

function DeliverableCard({ deliverable, onSaved }) {
  const [f, setF] = useState({
    status: deliverable.status || "Requested",
    submitted_date: deliverable.submitted_date || "",
    supplier_comment: deliverable.supplier_comment || "",
  });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/supplier/deliverables/${deliverable.id}`, f);
      toast.success("Deliverable updated");
      onSaved();
    } catch (e) { toast.error("Could not update deliverable"); }
    finally { setSaving(false); }
  };
  return (
    <Card className="p-5" data-testid={`deliverable-card-${deliverable.id}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="text-white font-medium text-sm">{deliverable.deliverable_name}</div>
        <div className="text-[11px] text-zinc-500 font-mono-data">{deliverable.deliverable_type} · Due {deliverable.due_date || "—"}</div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Status">
          <Select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} data-testid={`deliverable-status-${deliverable.id}`}>
            {DELIV_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </Select>
        </Field>
        <Field label="Submitted Date">
          <Input type="date" value={f.submitted_date} onChange={(e) => setF({ ...f, submitted_date: e.target.value })} data-testid={`deliverable-submitted-${deliverable.id}`} />
        </Field>
        <Field label="Comment to +++ Team" className="md:col-span-2">
          <Textarea rows={2} value={f.supplier_comment} onChange={(e) => setF({ ...f, supplier_comment: e.target.value })} data-testid={`deliverable-comment-${deliverable.id}`} />
        </Field>
      </div>
      <div className="mt-4">
        <PrimaryButton onClick={save} disabled={saving} data-testid={`deliverable-save-${deliverable.id}`}>{saving ? "Saving…" : "Save Deliverable"}</PrimaryButton>
      </div>
    </Card>
  );
}

function ProductionCard({ prod, onSaved }) {
  const [f, setF] = useState({
    capacity_confirmed: !!prod.capacity_confirmed,
    lead_time_days: prod.lead_time_days ?? "",
    production_start_estimate: prod.production_start_estimate || "",
    sample_dependency_status: prod.sample_dependency_status || "Awaiting Sample Approval",
    packaging_readiness: prod.packaging_readiness || "Not Ready",
    compliance_document_status: prod.compliance_document_status || "Pending",
    production_comment: prod.production_comment || "",
  });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...f, lead_time_days: f.lead_time_days === "" ? null : Number(f.lead_time_days) };
      await api.patch(`/supplier/production/${prod.id}`, payload);
      toast.success("Production readiness updated");
      onSaved();
    } catch (e) { toast.error("Could not update production readiness"); }
    finally { setSaving(false); }
  };
  return (
    <Card className="p-5" data-testid={`production-card-${prod.id}`}>
      <div className="text-white font-medium text-sm mb-4">{prod.production_tracker_name}</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Capacity Confirmed">
          <Select value={f.capacity_confirmed ? "yes" : "no"} onChange={(e) => setF({ ...f, capacity_confirmed: e.target.value === "yes" })} data-testid="prod-capacity-confirmed">
            <option value="no">No</option>
            <option value="yes">Yes</option>
          </Select>
        </Field>
        <Field label="Lead Time (days)">
          <Input type="number" value={f.lead_time_days} onChange={(e) => setF({ ...f, lead_time_days: e.target.value })} data-testid="prod-lead-time" />
        </Field>
        <Field label="Production Start Estimate">
          <Input type="date" value={f.production_start_estimate} onChange={(e) => setF({ ...f, production_start_estimate: e.target.value })} data-testid="prod-start" />
        </Field>
        <Field label="Sample Dependency">
          <Select value={f.sample_dependency_status} onChange={(e) => setF({ ...f, sample_dependency_status: e.target.value })} data-testid="prod-sample-dep">
            {DEP_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </Select>
        </Field>
        <Field label="Packaging Readiness">
          <Select value={f.packaging_readiness} onChange={(e) => setF({ ...f, packaging_readiness: e.target.value })} data-testid="prod-packaging">
            {READY_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </Select>
        </Field>
        <Field label="Compliance Document Status">
          <Select value={f.compliance_document_status} onChange={(e) => setF({ ...f, compliance_document_status: e.target.value })} data-testid="prod-compliance">
            {COMPLIANCE_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </Select>
        </Field>
        <Field label="Comment to +++ Team" className="md:col-span-2">
          <Textarea rows={2} value={f.production_comment} onChange={(e) => setF({ ...f, production_comment: e.target.value })} data-testid="prod-comment" />
        </Field>
      </div>
      <div className="mt-4">
        <PrimaryButton onClick={save} disabled={saving} data-testid="prod-save">{saving ? "Saving…" : "Save Readiness"}</PrimaryButton>
      </div>
    </Card>
  );
}

function QuestionsPanel({ questions, opportunityId, onSaved }) {
  const [subject, setSubject] = useState("");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const ask = async () => {
    if (!subject.trim() || !text.trim()) { toast.error("Subject and question are required"); return; }
    setBusy(true);
    try {
      await api.post("/supplier/questions", { opportunity_id: opportunityId, subject, question_text: text });
      toast.success("Question submitted");
      setSubject(""); setText(""); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not submit question"); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-4">
      {(questions || []).map((q) => <QuestionCard key={q.id} q={q} onSaved={onSaved} />)}
      <Card className="p-5" data-testid="ask-question-card">
        <div className="text-[10px] tracking-widest uppercase text-zinc-500 mb-3">Ask The +++ Team A Question</div>
        <div className="space-y-3">
          <Field label="Subject"><Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="question-ask-subject" /></Field>
          <Field label="Question"><Textarea rows={3} value={text} onChange={(e) => setText(e.target.value)} data-testid="question-ask-text" /></Field>
          <PrimaryButton onClick={ask} disabled={busy} data-testid="question-ask-submit">{busy ? "Sending…" : "Submit Question"}</PrimaryButton>
        </div>
      </Card>
    </div>
  );
}

function QuestionCard({ q, onSaved }) {
  const [reply, setReply] = useState(q.supplier_comment || "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!reply.trim()) { toast.error("Reply cannot be empty"); return; }
    setBusy(true);
    try {
      await api.patch(`/supplier/questions/${q.id}`, { supplier_comment: reply });
      toast.success("Reply saved");
      onSaved();
    } catch (e) { toast.error("Could not save reply"); }
    finally { setBusy(false); }
  };
  return (
    <Card className="p-5" data-testid={`question-card-${q.id}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-white font-medium text-sm">{q.question_title}</div>
        <StatusPill value={q.question_status} />
      </div>
      <div className="text-xs text-zinc-300 mb-3">{q.question}</div>
      {q.answer && (
        <div className="border-l-2 border-[#00E676] pl-3 py-1 mb-3">
          <div className="text-[10px] tracking-widest uppercase text-[#00E676] mb-0.5">+++ Team Answer</div>
          <div className="text-xs text-zinc-200">{q.answer}</div>
        </div>
      )}
      <Field label="Your Reply / Comment">
        <Textarea rows={2} value={reply} onChange={(e) => setReply(e.target.value)} data-testid={`question-reply-${q.id}`} />
      </Field>
      <div className="mt-3">
        <GhostButton onClick={save} disabled={busy} data-testid={`question-reply-save-${q.id}`}>{busy ? "Saving…" : "Save Reply"}</GhostButton>
      </div>
    </Card>
  );
}

function DocsPanel({ docs }) {
  const download = async (doc) => {
    if (doc.source_url) { window.open(doc.source_url, "_blank", "noreferrer"); return; }
    try {
      const res = await api.get(`/files/${doc.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.original_filename || doc.document_name || "document";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("File not available"); }
  };
  if (!docs || docs.length === 0) return <Empty>No documents shared with you yet.</Empty>;
  return (
    <Card className="p-2" data-testid="supplier-docs">
      {docs.map((doc) => (
        <div key={doc.id} className="flex items-center justify-between px-3 py-3 border-b border-white/5 last:border-0">
          <div>
            <div className="text-sm text-zinc-200">{doc.document_name}</div>
            <div className="text-[10px] tracking-widest uppercase text-zinc-500 mt-0.5">{doc.document_type} · {doc.document_status}</div>
          </div>
          <GhostButton onClick={() => download(doc)} data-testid={`doc-download-${doc.id}`}>
            <DownloadSimple size={13} /> {doc.source_url ? "Open" : "Download"}
          </GhostButton>
        </div>
      ))}
    </Card>
  );
}
